import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut, Loader2, Move } from "lucide-react";

const VIEW = 288;   // on-screen square viewport (px)
const OUT = 512;    // exported square size (px)

// Lightweight square cropper with drag-to-reposition + zoom. Exports a
// re-rendered JPEG (the server independently re-encodes + strips metadata).
export default function ImageCropModal({ file, open, onCancel, onConfirm, busy }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    const im = new Image();
    im.onload = () => {
      imgRef.current = im;
      setNat({ w: im.naturalWidth, h: im.naturalHeight });
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const baseScale = nat.w && nat.h ? Math.max(VIEW / nat.w, VIEW / nat.h) : 1;
  const eff = baseScale * zoom;
  const dispW = nat.w * eff;
  const dispH = nat.h * eff;

  const clamp = useCallback((o) => {
    const minX = VIEW - dispW;
    const minY = VIEW - dispH;
    return {
      x: Math.min(0, Math.max(minX, o.x)),
      y: Math.min(0, Math.max(minY, o.y)),
    };
  }, [dispW, dispH]);

  useEffect(() => { setOffset((o) => clamp(o)); }, [zoom, clamp]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.px;
    const dy = e.clientY - dragRef.current.py;
    setOffset(clamp({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const confirm = () => {
    const im = imgRef.current;
    if (!im) return;
    const sourceSize = VIEW / eff;
    const sx = -offset.x / eff;
    const sy = -offset.y / eff;
    const canvas = document.createElement("canvas");
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(im, sx, sy, sourceSize, sourceSize, 0, 0, OUT, OUT);
    canvas.toBlob((blob) => {
      if (blob) onConfirm(blob);
    }, "image/jpeg", 0.9);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="max-w-sm" data-testid="profile-crop-modal">
        <DialogHeader>
          <DialogTitle className="font-serif">Position your photo</DialogTitle>
        </DialogHeader>
        <div
          className="relative mx-auto overflow-hidden rounded-full border-2 border-[hsl(var(--pc-gold)/0.5)] bg-[hsl(var(--pc-surface2))] touch-none cursor-grab active:cursor-grabbing"
          style={{ width: VIEW, height: VIEW }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="profile-crop-viewport"
        >
          {imgUrl && (
            <img
              src={imgUrl}
              alt="Crop preview"
              draggable={false}
              style={{
                position: "absolute",
                width: dispW, height: dispH,
                transform: `translate(${offset.x}px, ${offset.y}px)`,
                maxWidth: "none",
              }}
            />
          )}
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <Move className="h-5 w-5 text-white/40" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2 px-1">
          <ZoomOut className="h-4 w-4 text-cream-dim shrink-0" />
          <Slider
            value={[zoom]} min={1} max={3} step={0.01}
            onValueChange={(v) => setZoom(v[0])}
            data-testid="profile-crop-zoom"
            aria-label="Zoom"
          />
          <ZoomIn className="h-4 w-4 text-cream-dim shrink-0" />
        </div>
        <p className="text-[11px] text-cream-dim text-center">Drag to reposition · pinch or slide to zoom</p>
        <DialogFooter className="mt-1 gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy} data-testid="profile-crop-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={busy || !nat.w} data-testid="profile-crop-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Use photo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
